import { useEffect, useRef, useState } from "react";
import axios from "axios";

const routes = {
  air: ["air-quality", "air-quality-forecast"],
  climate: ["climate"],
  flood: ["flood"],
  veg: ["vegetation"],
  drought: ["drought"],
};
export const REFRESH_MS = 5 * 60 * 1000;

export default function useEnvironmentalData(moduleId, lat, lon, refreshKey) {
  const [state, setState] = useState({
    key: "",
    loading: true,
    feeds: [],
    errors: [],
  });
  const sequence = useRef(0);
  const key = `${moduleId}:${lat}:${lon}:${refreshKey}`;
  useEffect(() => {
    let controller;
    let disposed = false;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      const requestId = ++sequence.current;
      setState((previous) =>
        previous.key === key
          ? { ...previous, loading: true }
          : { key, loading: true, feeds: [], errors: [] },
      );
      const results = await Promise.allSettled(
        routes[moduleId].map((route) =>
          axios.get(`/api/${route}`, {
            params: { lat, lon },
            signal: controller.signal,
            timeout: 20000,
          }),
        ),
      );
      if (disposed || requestId !== sequence.current) return;
      const feeds = [],
        errors = [];
      results.forEach((result, i) => {
        const route = routes[moduleId][i];
        if (
          result.status === "fulfilled" &&
          typeof result.value.data === "object" &&
          result.value.data.source &&
          !result.value.data.error
        ) {
          feeds.push({ route, ...result.value.data });
        } else {
          errors.push(
            `${route}: ${result.status === "rejected" ? result.reason.response?.data?.error || "Request failed. Please retry." : "Unexpected API response."}`,
          );
        }
      });
      setState({
        key,
        loading: false,
        feeds,
        errors,
        checkedAt: new Date().toISOString(),
      });
    };
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, REFRESH_MS);
    const resume = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", resume);
    return () => {
      disposed = true;
      controller?.abort();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [key, moduleId, lat, lon]);
  // Never show a previous location or module while its replacement is loading.
  return state.key === key
    ? state
    : { key, loading: true, feeds: [], errors: [] };
}
